import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, Save } from "lucide-react";
import { invalidatePlatformMessages } from "@/lib/usePlatformMessages";

export default function AdminMessages() {
  const { t } = useI18n();
  const [msgs, setMsgs] = useState([]);
  const [query, setQuery] = useState("");

  const load = async () => {
    const { data } = await api.get("/messages");
    // Sort alphabetically by key for stable browsing
    const sorted = [...data].sort((a, b) => (a.key || "").localeCompare(b.key || ""));
    setMsgs(sorted);
  };
  useEffect(() => { load(); }, []);

  const save = async (m) => {
    await api.put(`/messages/${m.id}`, { content: m.content });
    invalidatePlatformMessages();
    toast.success("Guardado");
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return msgs;
    return msgs.filter((m) => {
      if ((m.key || "").toLowerCase().includes(q)) return true;
      if ((m.description || "").toLowerCase().includes(q)) return true;
      const c = m.content || {};
      return ["pt", "en", "es"].some((l) => (c[l] || "").toLowerCase().includes(q));
    });
  }, [msgs, query]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-[Outfit] font-semibold text-slate-900">{t("messages")}</h1>
          <p className="text-sm text-slate-500 mt-1">
            Todas as mensagens de aviso, sucesso e erro mostradas aos utilizadores. Pesquisa por chave, descrição ou conteúdo (PT/EN/ES).
          </p>
        </div>
      </div>

      {/* Search bar */}
      <div className="bg-white border border-slate-200 rounded-2xl p-3 mb-4 flex items-center gap-3">
        <Search className="w-4 h-4 text-slate-400 ml-2" />
        <Input
          data-testid="messages-search"
          placeholder="Pesquisar por palavra (ex: tentativas, login, diploma)..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        <div className="text-xs text-slate-500 whitespace-nowrap pr-3">
          {filtered.length} de {msgs.length}
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map((m, i) => {
          const idxOriginal = msgs.findIndex((x) => x.id === m.id);
          return (
            <div key={m.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`message-row-${m.key}`}>
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500 font-mono">{m.key}</div>
                  {m.description && (
                    <div className="text-xs text-slate-500 mt-0.5 italic">{m.description}</div>
                  )}
                </div>
                <Button size="sm" onClick={() => save(m)} data-testid={`btn-save-${m.key}`} className="bg-slate-900">
                  <Save className="w-3.5 h-3.5 mr-1" /> {t("save")}
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {["pt", "en", "es"].map((lang) => (
                  <div key={lang}>
                    <Label className="text-xs">{lang.toUpperCase()}</Label>
                    <Input
                      data-testid={`msg-${m.key}-${lang}`}
                      value={m.content?.[lang] || ""}
                      onChange={(e) => {
                        const arr = [...msgs];
                        if (idxOriginal >= 0) {
                          arr[idxOriginal] = { ...arr[idxOriginal], content: { ...(arr[idxOriginal].content || {}), [lang]: e.target.value } };
                          setMsgs(arr);
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {!filtered.length && (
          <div className="text-center py-10 text-slate-400 bg-white border border-slate-200 rounded-2xl">
            Nenhuma mensagem encontrada para <b>{query}</b>
          </div>
        )}
      </div>
    </div>
  );
}
